import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4 sm:p-6" }
const _hoisted_2 = { class: "w-full max-w-md" }
const _hoisted_3 = { class: "bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-8" }
const _hoisted_4 = { class: "flex justify-center mb-4" }
const _hoisted_5 = { class: "p-3 bg-[#22282F]/10 rounded-xl" }
const _hoisted_6 = {
  key: 0,
  class: "mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"
}
const _hoisted_7 = { class: "text-sm text-red-800" }
const _hoisted_8 = { class: "flex items-center justify-center gap-2 sm:gap-3" }
const _hoisted_9 = ["value", "onInput", "onKeydown", "onPaste", "disabled"]
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { key: 1 }

import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ShieldCheck, AlertCircle, ArrowLeft } from 'lucide-vue-next'
import { useAuthStore } from '@/store/auth'


export default /*@__PURE__*/_defineComponent({
  __name: 'OtpView',
  setup(__props) {

const store = useAuthStore()
const router = useRouter()

const otp = ref<string[]>(Array(6).fill(''))
const otpRefs = ref<Array<HTMLInputElement | null>>([])
const isLoading = ref(false)
const error = ref('')

const isComplete = computed(() => otp.value.every((d) => /^\d$/.test(d)))

// Si aucun pending_token (accès direct à /otp), retour au login
onMounted(() => {
  if (!store.pendingToken) {
    router.replace('/')
    return
  }
  focusIndex(0)
})

function setOtpRef(el: any, idx: number) {
  otpRefs.value[idx] = (el as HTMLInputElement) || null
}

function focusIndex(i: number) {
  const el = otpRefs.value[i]
  el?.focus()
  el?.select()
}

function distribute(digits: string) {
  const ds = digits.replace(/\D/g, '').slice(0, 6)
  for (let i = 0; i < 6; i++) otp.value[i] = ds[i] || ''
  const firstEmpty = otp.value.findIndex((d) => !d)
  focusIndex(firstEmpty === -1 ? 5 : firstEmpty)
}

function handleInput(e: Event, idx: number) {
  const target = e.target as HTMLInputElement
  const val = (target.value || '').replace(/\D/g, '')
  if (val.length > 1) {
    distribute(val)
    return
  }
  otp.value[idx] = val
  if (val && idx < 5) focusIndex(idx + 1)
}

function onKeydown(e: KeyboardEvent, idx: number) {
  const key = e.key
  if (key === 'Backspace') {
    if (otp.value[idx]) {
      otp.value[idx] = ''
    } else if (idx > 0) {
      otp.value[idx - 1] = ''
      focusIndex(idx - 1)
    }
    e.preventDefault()
  } else if (key === 'ArrowLeft' && idx > 0) {
    focusIndex(idx - 1)
    e.preventDefault()
  } else if (key === 'ArrowRight' && idx < 5) {
    focusIndex(idx + 1)
    e.preventDefault()
  }
}

function onPaste(e: ClipboardEvent) {
  const pasted = e.clipboardData?.getData('text') || ''
  const digits = pasted.replace(/\D/g, '')
  if (!digits) return
  e.preventDefault()
  distribute(digits)
}

function resetCode() {
  otp.value = Array(6).fill('')
  focusIndex(0)
}

async function verify() {
  const code = otp.value.join('')
  if (!/^\d{6}$/.test(code)) return

  error.value = ''
  isLoading.value = true
  try {
    const result = await store.verifyLogin(code)
    if (result.status === 'success') {
      router.push('/dashboard')
    } else if (result.status === 'expired') {
      error.value = result.error
      setTimeout(() => router.replace('/'), 1500)
    } else {
      error.value = (result as any).error || 'Code incorrect'
      resetCode()
    }
  } catch {
    error.value = 'Erreur de connexion au serveur'
  } finally {
    isLoading.value = false
  }
}

function backToLogin() {
  router.replace('/')
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[5] || (_cache[5] = _createElementVNode("div", { class: "text-center mb-6 sm:mb-8" }, [
        _createElementVNode("div", { class: "flex justify-center mb-4" }, [
          _createElementVNode("img", {
            src: "/logo.png",
            alt: "Logo",
            class: "h-16 sm:h-20 w-auto object-contain"
          })
        ])
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_unref(ShieldCheck), { class: "w-7 h-7 text-[#22282F]" })
          ])
        ]),
        _cache[3] || (_cache[3] = _createElementVNode("h2", { class: "text-xl sm:text-2xl font-bold text-gray-900 mb-1 text-center" }, " Vérification en deux étapes ", -1)),
        _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-gray-500 text-sm text-center mb-6" }, " Saisissez le code à 6 chiffres généré par votre application d'authentification (Google Authenticator, Authy…). ", -1)),
        (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(_unref(AlertCircle), { class: "w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" }),
              _createElementVNode("p", _hoisted_7, _toDisplayString(error.value), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("form", {
          onSubmit: _withModifiers(verify, ["prevent"]),
          class: "space-y-6"
        }, [
          _createElementVNode("div", _hoisted_8, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (_, i) => {
              return _createElementVNode("input", {
                key: i,
                value: otp.value[i],
                onInput: ($event: any) => (handleInput($event, i)),
                onKeydown: ($event: any) => (onKeydown($event, i)),
                onPaste: ($event: any) => (onPaste($event, i)),
                inputmode: "numeric",
                autocomplete: "one-time-code",
                pattern: "[0-9]*",
                type: "tel",
                disabled: isLoading.value,
                class: "w-11 h-14 sm:w-12 sm:h-14 text-center text-lg font-semibold rounded-xl border border-gray-300 bg-gray-50 text-gray-900 focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/15 transition-all disabled:opacity-50",
                ref_for: true,
                ref: el => setOtpRef(el, i),
                maxlength: "1"
              }, null, 40, _hoisted_9)
            }), 64))
          ]),
          _createElementVNode("button", {
            type: "submit",
            disabled: isLoading.value || !isComplete.value,
            class: "w-full py-3 bg-[#22282F] text-white rounded-lg text-sm font-medium hover:bg-[#2a3139] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          }, [
            (isLoading.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _cache[0] || (_cache[0] = _createElementVNode("div", { class: "w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" }, null, -1)),
                  _cache[1] || (_cache[1] = _createElementVNode("span", null, "Vérification…", -1))
                ], 64))
              : (_openBlock(), _createElementBlock("span", _hoisted_11, "Vérifier"))
          ], 8, _hoisted_10)
        ], 32),
        _createElementVNode("button", {
          type: "button",
          onClick: backToLogin,
          class: "mt-5 w-full text-sm text-gray-500 hover:text-[#22282F] transition-colors flex items-center justify-center gap-1"
        }, [
          _createVNode(_unref(ArrowLeft), { class: "w-4 h-4" }),
          _cache[2] || (_cache[2] = _createTextVNode(" Revenir à la connexion "))
        ])
      ]),
      _cache[6] || (_cache[6] = _createElementVNode("div", { class: "mt-6 text-center" }, [
        _createElementVNode("p", { class: "text-xs text-gray-400" }, "v1.0 • © 2025 Ministère des Affaires Étrangères")
      ], -1))
    ])
  ]))
}
}

})